import { User } from '@/client/users';
import { UserOrigin } from '@/client/users/types/origins.enum';
import { LoadingStatuses } from '@/common/constants';
import { PERMANENT_DELETE_CONFIRMATION_TOKEN } from '@/common/constants/user';
import { AppButton } from '@/ui/buttons';
import { WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  &.p-dialog {
    width: 508px;
    border-radius: var(--medium-border-radius);

    .p-dialog-header {
      display: none;
    }

    .p-dialog-content {
      padding: var(--semi-big-padding);
      padding-bottom: var(--default-padding);
    }

    .p-dialog-footer {
      padding: var(--semi-big-padding);
      padding-top: var(--default-padding);
      display: flex;
      gap: var(--semi-xsmall-padding);
    }
  }
`;

const StyledInput = styled(InputText)`
  width: 100%;
  background-color: var(--beige-main);
  border: none;
  padding: var(--default-padding);

  &:focus {
    box-shadow: none;
    border: none;
  }
`;

const ModalHeading = styled.h3`
  font-size: var(--modal-heading-font-size);
  font-weight: 700;
  line-height: 1.4;
  margin: 0;
`;

const ModalBody = styled.p`
  margin-top: var(--semi-xsmall-padding);
`;

const TypeConfirmLabel = styled.p`
  margin-top: var(--default-padding);
`;

const AdSyncWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--small-padding);
  margin-top: var(--semi-big-padding);
  padding: var(--medium-padding) var(--default-padding);
  padding-left: var(--small-padding);
  background: var(--yellow-warning);
  border-radius: var(--box-border-radius);

  svg {
    flex-shrink: 0;
  }

  p {
    font-size: var(--small-font-size);
    font-weight: 500;
    color: var(--black-main);
    line-height: var(--small-line-height);
    margin: 0;
  }
`;

type BulkPermanentDeleteModalProps = {
  visible: boolean;
  users: User[];
  deleting: boolean;
  onConfirm: (users: User[]) => void;
  onClose: () => void;
};

export const BulkPermanentDeleteModal: React.FC<
  BulkPermanentDeleteModalProps
> = ({ visible, users, deleting, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (!visible) return;
    setConfirmText('');
  }, [visible]);

  const isConfirmed = confirmText === PERMANENT_DELETE_CONFIRMATION_TOKEN;
  const count = users.length;
  const hasAdSyncUsers = users.some(
    (user) => user.origin === UserOrigin.AZURE_AD,
  );

  return (
    <StyledDialog
      visible={visible}
      onHide={onClose}
      closable={false}
      draggable={false}
      footer={
        <>
          <AppButton
            label={t('dialog.permanentDelete.user.confirm')}
            severity="danger"
            state={deleting ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
            isDisabled={!isConfirmed || deleting}
            onClick={() => {
              if (isConfirmed) {
                onConfirm(users);
              }
            }}
          />
          <AppButton
            label={t('button.cancel')}
            severity="secondary"
            type="outlined"
            isDisabled={deleting}
            onClick={onClose}
          />
        </>
      }
    >
      <ModalHeading>
        {t('dialog.permanentDelete.users.header', { count })}
      </ModalHeading>
      <ModalBody>
        <Trans
          i18nKey="dialog.permanentDelete.users.body"
          values={{ count }}
          components={{ bold: <strong /> }}
        />
      </ModalBody>
      {hasAdSyncUsers && (
        <AdSyncWarning>
          <WarningTriangleIcon />
          <p>{t('dialog.delete.bulk.adSyncWarning')}</p>
        </AdSyncWarning>
      )}
      <TypeConfirmLabel>
        {t('dialog.permanentDelete.user.typeConfirm', {
          token: PERMANENT_DELETE_CONFIRMATION_TOKEN,
        })}
      </TypeConfirmLabel>
      <StyledInput
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder={t('dialog.permanentDelete.user.inputPlaceholder')}
        autoFocus
      />
    </StyledDialog>
  );
};
